import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { ArrowBack, ChevronRight } from '@mui/icons-material';

import GoalInfo from './GoalInfo';
import IndicatorDetails from './IndicatorDetails';
import TargetAssessment from './TargetAssessment';
import SDGSpinner from './SDGSpinner';
import { GoalsContext } from '../State/GoalsContext';
import { TargetsContext } from '../State/TargetsContext';
import { normalizeGoalType, goalTypeToUrl } from '../utils/goalTypeUtils';
import { getStatusColor } from '../utils/euStatusUtils';

// Helper to get trend arrow
const getTrendArrow = (category) => {
  if (!category) return { arrow: '—', color: '#bdbdbd' };
  const cat = String(category).toLowerCase();
  if (cat.includes('on track') || cat.includes('target met')) return { arrow: '↑', color: '#16a34a' };
  if (cat.includes('moderate')) return { arrow: '↗', color: '#fcc30c' };
  if (cat.includes('marginal') || cat.includes('stagnation') || cat.includes('stagnating')) return { arrow: '→', color: '#f67c01' };
  if (cat.includes('regression') || cat.includes('decreasing')) return { arrow: '↓', color: '#d3302f' };
  return { arrow: '—', color: '#bdbdbd' };
};

const GoalDetailMobile = () => {
  const { selectedGoal, fetchGoal, loading: goalLoading, goalType: contextGoalType } = useContext(GoalsContext);
  const { targets, loading: targetsLoading, fetchTargetsByGoal } = useContext(TargetsContext);
  const navigate = useNavigate();

  const { goalType: urlGoalType, goalId, targetId } = useParams();
  // Memoize goalType to prevent unnecessary re-renders
  const goalType = useMemo(() => normalizeGoalType(urlGoalType || contextGoalType), [urlGoalType, contextGoalType]);
  const [selectedTargetId, setSelectedTargetId] = useState(targetId || null);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [view, setView] = useState(targetId ? 'detail' : 'list'); // 'list' or 'detail'

  // Scroll to top when opening targets view or when goal/target changes (mobile)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [goalId, targetId]);

  // Fetch goal (goals are shared between EU/UN, no type needed)
  useEffect(() => {
    const parsedGoalId = parseInt(goalId);
    if (goalId && (!selectedGoal || selectedGoal.id !== parsedGoalId)) {
      fetchGoal(parsedGoalId);
    }
    // Removed selectedGoal from dependencies to prevent unnecessary re-fetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalId, fetchGoal, selectedGoal?.id]);

  // Use a ref to track the last successfully fetched combination
  const lastFetchedKeyRef = useRef(null);
  const initiatedFetchRef = useRef(null);
  const lastProcessedGoalIdRef = useRef(null);
  const lastProcessedGoalTypeRef = useRef(null);
  
  // Fetch targets when goal is loaded - only when we have a valid selectedGoal
  useEffect(() => {
    // CRITICAL: Don't run if selectedGoal is null (happens when fetchGoal clears it)
    if (!selectedGoal || !selectedGoal.id) {
      return;
    }
    
    const currentGoalId = selectedGoal.id;
    const currentGoalType = goalType;
    
    // Early return if we don't have goalType
    if (!currentGoalType) {
      return;
    }
    
    // Check if goalId or goalType actually changed (not just a re-render)
    const goalIdChanged = lastProcessedGoalIdRef.current !== currentGoalId;
    const goalTypeChanged = lastProcessedGoalTypeRef.current !== currentGoalType;
    
    // Update tracking refs
    lastProcessedGoalIdRef.current = currentGoalId;
    lastProcessedGoalTypeRef.current = currentGoalType;
    
    // Only proceed if values actually changed
    if (!goalIdChanged && !goalTypeChanged) {
      return;
    }
    
    // Create a stable cache key
    const cacheKey = `${currentGoalId}-${currentGoalType}`;
    
    // Early return if we've already successfully fetched this exact combination
    if (lastFetchedKeyRef.current === cacheKey) {
      console.log(`[GoalDetailMobile] Already fetched ${cacheKey}, skipping`);
      return;
    }
    
    // Early return if we've already initiated a fetch for this exact combination
    // Check and set atomically to prevent race conditions
    if (initiatedFetchRef.current === cacheKey) {
      console.log(`[GoalDetailMobile] Already initiated fetch for ${cacheKey}, skipping`);
      return;
    }
    
    // Mark as initiated AND fetched IMMEDIATELY and SYNCHRONOUSLY to prevent race conditions
    // This must happen before any async operations
    // We mark as "fetched" immediately because TargetsProvider will handle the actual caching
    initiatedFetchRef.current = cacheKey;
    lastFetchedKeyRef.current = cacheKey; // Set immediately to prevent re-fetch on re-render
    console.log(`[GoalDetailMobile] Initiating fetch for ${cacheKey}`);
    
    // Fetch targets - TargetsProvider will handle promise caching and prevent duplicates
    fetchTargetsByGoal(currentGoalId, currentGoalType)
      .then(() => {
        console.log(`[GoalDetailMobile] Successfully fetched ${cacheKey}`);
        // Clear initiated flag - lastFetchedKeyRef is already set above
        if (initiatedFetchRef.current === cacheKey) {
          initiatedFetchRef.current = null;
        }
      })
      .catch((error) => {
        console.error('Error fetching targets:', error);
        // On error, clear both flags to allow retry
        if (initiatedFetchRef.current === cacheKey) {
          initiatedFetchRef.current = null;
        }
        if (lastFetchedKeyRef.current === cacheKey) {
          lastFetchedKeyRef.current = null;
        }
      });
    
    // Cleanup: if component unmounts or effect re-runs, we keep the refs
    // to prevent duplicate fetches
    // Removed fetchTargetsByGoal and targetsLoading from dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGoal?.id, goalType]);

  // Handle selected target
  useEffect(() => {
    if (!targetId || targets.length === 0) return;

    const parsedId = parseInt(targetId);
    const match = targets.find(t => t.id === parsedId);

    if (match) {
      setSelectedTargetId(parsedId);
      setSelectedTarget(match);
      setView('detail');
    } else {
      setSelectedTarget(null);
      setView('list');
    }
  }, [targetId, targets]);

  const handleTargetClick = useCallback((target) => {
    // Prevent rapid clicks during loading
    if (targetsLoading) return;
    
    setSelectedTargetId(target.id);
    setSelectedTarget(target);
    setView('detail');
    navigate(`/${goalTypeToUrl(goalType)}/goal/${goalId}/targets/${target.id}`);
  }, [goalId, goalType, navigate, targetsLoading]);

  const handleBackToList = useCallback(() => {
    setView('list');
    setSelectedTargetId(null);
    setSelectedTarget(null);
    navigate(`/${goalTypeToUrl(goalType)}/goal/${goalId}/targets`);
  }, [goalId, goalType, navigate]);

  const handleBackToGoals = useCallback(() => {
    navigate(`/${goalTypeToUrl(goalType)}`);
  }, [goalType, navigate]);

  // Show loading spinner while fetching goal or targets
  if (goalLoading || targetsLoading || !selectedGoal) {
    return <SDGSpinner size="lg" text="Loading goal details..." />;
  }

  if (!selectedGoal) return <p>Goal not found</p>;

  return (
    <div className="mobile-goal-detail-container">
      {view === 'list' && (
        <>
          <div className="mobile-header">
            <Button
              variant="link"
              onClick={handleBackToGoals}
              className="mobile-back-btn"
            >
              <ArrowBack fontSize="small" /> Back to Goals
            </Button>
          </div>

          <div className="mobile-goal-info">
            <GoalInfo goal={selectedGoal} />
          </div>

          {/* Targets List */}
          <div className="mobile-section">
            <div className="mobile-section-header">
              <h5>Targets ({targets.length})</h5>
            </div>
            
            <div className="mobile-list">
              {targets.map((target) => (
                <div 
                  key={target.id}
                  className="mobile-list-item"
                  onClick={() => handleTargetClick(target)}
                >
                  <div className="mobile-list-content">
                    <div className="mobile-list-main">
                      <div className="mobile-list-title">
                        <strong>{target.acf.index}</strong>
                      </div>
                      <div className="mobile-list-subtitle">
                        {target.title_render}
                      </div>
                    </div>
                    <div className="mobile-list-status" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                      {/* Semaphore circle */}
                      {/* Status - Hidden for UN indicators */}
                      {goalType !== 'un' && (
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(target?.computed?.target_semaphore_status),
                          border: '1px solid #fff',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        }}
                        title={target?.computed?.target_semaphore_status || 'Status not available'}
                      />
                      )}
                      {/* Trend arrow */}
                      {target?.computed?.target_trend_status && (() => {
                        const { arrow, color } = getTrendArrow(target.computed.target_trend_status);
                        return (
                          <span
                            className="mobile-list-trend"
                            style={{ color }}
                            title={target.computed.target_trend_status}
                          >
                            {arrow}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="mobile-list-arrow">
                      <ChevronRight />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {view === 'detail' && selectedTarget && (
        <>
          <div className="mobile-header">
            <Button
              variant="link"
              onClick={handleBackToList}
              className="mobile-back-btn"
            >
              <ArrowBack fontSize="small" /> Back to Targets
            </Button>
          </div>

          {/* Target Detail with Indicators */}
          <div className="mobile-detail-content">
            <div className="mobile-target-info">
              <h4>
                <strong>TARGET {selectedTarget.acf.index} –</strong> {selectedTarget.title || selectedTarget.title_render}
              </h4>
            </div>
            
            <TargetAssessment target={selectedTarget} />
            
            <IndicatorDetails 
              selectedTarget={selectedTarget} 
              selectedTargetId={selectedTargetId} 
            />
          </div>
        </>
      )}
    </div>
  );
};

export default GoalDetailMobile;
